import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAllOrganizations } from "../../services/service";
import DataGrid from "../../atoms/DataTable/DataGrid";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { useNavigate } from "react-router-dom";
import { getOrganizationDisplayPlan } from "../../utils/subscriptionPlanDisplay";

const AllOrganizations = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const rowsPerPageRef = useRef(rowsPerPage);

  useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);

  const fetchOrganizations = useCallback(
    async (page = 1, limit) => {
      const effectiveLimit = limit ?? rowsPerPageRef.current;
      const planParam = planFilter !== "all" ? planFilter : undefined;
      const statusParam =
        statusFilter === "active"
          ? "true"
          : statusFilter === "inactive"
            ? "false"
            : undefined;
      const searchParam = debouncedSearch?.trim() || undefined;

      setLoading(true);
      try {
        const response = await getAllOrganizations(page, effectiveLimit, {
          plan: planParam,
          search: searchParam,
          is_active: statusParam,
        });
        if (response?.success) {
          setOrganizations(response.data || []);
          if (response.pagination) {
            const nextPageSize =
              response.pagination.itemsPerPage || effectiveLimit;
            setRowsPerPage(nextPageSize);
            setPagination({
              current: response.pagination.currentPage,
              pageSize: nextPageSize,
              total: response.pagination.totalItems,
            });
          } else {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: effectiveLimit,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
      setLoading(false);
    },
    [planFilter, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchOrganizations(1, rowsPerPageRef.current);
  }, [fetchOrganizations]);

  const handlePageChange = (newPage, newPageSize) => {
    setRowsPerPage(newPageSize);
    setPagination((prev) => ({
      ...prev,
      current: newPage,
      pageSize: newPageSize,
    }));
    fetchOrganizations(newPage, newPageSize);
  };

  const handleSearchInputChange = (value = "") => {
    setSearchTerm(value);
  };

  const handleFilterChange = (values = {}) => {
    const nextStatus = values?.status || "all";
    const nextPlan = values?.plan || "all";
    setStatusFilter(nextStatus);
    setPlanFilter(nextPlan);
  };

  const handleAddOrganization = () => {
    navigate("/super-admin/add-organization");
  };

  // Transform organizations data to match DG format
  const transformedData = organizations.map((org) => {
    const planDisplay = getOrganizationDisplayPlan(org);
    return {
      id: org.id,
      title: org.title,
      plan: planDisplay.code || "BASIC",
      status:
        org.is_active === true || org.is_active === 1
          ? "ACTIVE"
          : org.is_active === false
            ? "INACTIVE"
            : org.stripe_status?.toUpperCase() || "-",
      subscriptionEnd: org.subscription_end_date
        ? new Date(org.subscription_end_date).toLocaleDateString()
        : "-",
      createdAt: org.createdAt
        ? new Date(org.createdAt).toLocaleDateString()
        : "-",
      plan_changed_by: org.plan_changed_by || null,
    };
  });

  return (
    <div className="row px-[5px] py-4">
      <div className="col-12">
        <div className="row d-flex align-items-end">
          <div className="col-12">
            <ListHeader
              mainHeading="All Organizations"
              placeholder="Search by organization name"
              handleSearchChange={handleFilterChange}
              linkBreadCrum="/super-admin/all-organizations"
              blinkBreadCrumText="Organizations"
              blinkBreadCrumText1="All Organizations"
              searchShow={true}
              searchValue={searchTerm}
              handleProjectSearchChange={handleSearchInputChange}
              moduleType="super-admin-organizations"
              showFilter={true}
              DropDown={false}
              btnShow={false}
            />
          </div>
        </div>
      </div>

      {/* Add Organization Button */}
      <div className="w-full px-2">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleAddOrganization}
            className="w-[170px] py-1 rounded-[4px] bg-[#F97316] font-medium text-base text-white hover:bg-orange-600"
          >
            Add Organization
          </button>
        </div>
      </div>

      <div className="w-full px-2">
        <DataGrid
          index={10}
          loader={loading}
          data={transformedData}
          pagination={pagination}
          onPageChange={handlePageChange}
          getData={fetchOrganizations}
          hideActions={true}
        />
      </div>
    </div>
  );
};

export default AllOrganizations;
